import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getAPI, postAPI, reinitialiseAPI } from 'store/apiReducer';
import {
    Button,
    LoaderMi,
    Row,
    Col,
    Container,
    ButtonGroup,
    InputControl,
    ButtonLink,
} from '@si-armes/dsfr-react';
import usagerClientReducer from 'store/usagerReducer';
import {
    API_MODIFIER_QUESTIONS_REPONSES,
    API_RECUPERER_QUESTIONS,
    CONTEXT_API_MODIFIER_QUESTIONS_REPONSES,
    CONTEXT_API_RECUPERER_QUESTIONS,
} from 'constants/apiUrls.constants';
import SelectQuestionsCompteUsager from 'components/form/referentiel/select/SelectQuestionsCompteUsager';
import formaterModifierQuestionsReponses from './components/FormaterModifierQuestionsReponses';
import RoutesEcranGestion from '../gestion/routes';
import { getPath } from '../../../../utils/router';

const ModifierQuestionsReponses = ({
    postApi,
    getApi,
    reinitialiseApi,
    isLoadingRecupererQuestions,
    isLoadingModifierQuestionsReponses,
    dataQuestions,
}) => {
    const history = useHistory();
    const { register, handleSubmit, watch, formState, trigger, getValues, reset } = useForm({
        mode: 'onChange',
        criteriaMode: 'all',
    });

    const isStringNotEmpty = (stringToTest) => {
        return stringToTest !== '';
    };

    const isQuestionNotEmpty = () => {
        return (
            isStringNotEmpty(watch('question1')) &&
            isStringNotEmpty(watch('question2')) &&
            isStringNotEmpty(watch('question3'))
        );
    };

    const isReponseNotEmpty = () => {
        return (
            isStringNotEmpty(watch('reponse1')) &&
            isStringNotEmpty(watch('reponse2')) &&
            isStringNotEmpty(watch('reponse3'))
        );
    };

    const hasFormValid = () => {
        return isReponseNotEmpty() && isQuestionNotEmpty() && formState.isValid;
    };

    useEffect(() => {
        if (!isLoadingRecupererQuestions && dataQuestions) {
            reset(dataQuestions);
        }
    }, [dataQuestions]);

    useEffect(() => {
        reinitialiseApi(CONTEXT_API_MODIFIER_QUESTIONS_REPONSES);
        getApi(usagerClientReducer, API_RECUPERER_QUESTIONS, CONTEXT_API_RECUPERER_QUESTIONS);

        return () => {
            reinitialiseApi(CONTEXT_API_MODIFIER_QUESTIONS_REPONSES);
        };
    }, []);

    /**
     * Relance la validité des questions si une question est modifiée.
     */
    useEffect(() => {
        if (!getValues('question1') && !getValues('question2') && !getValues('question3')) return;
        trigger(['question1', 'question2', 'question3']);
    }, [watch('question1'), watch('question2'), watch('question3')]);

    /**
     * @returns {string|null} Renvoie un message d'erreur si un doublon existe.
     */
    const verifierDoublonQuestion = (questionModifiee, autreQuestion1, autreQuestion2) => {
        const error =
            (questionModifiee === autreQuestion1 || questionModifiee === autreQuestion2) &&
            !!questionModifiee;
        if (error) {
            return 'La question est déjà sélectionnée, veuillez en choisir une autre.';
        }
        return null;
    };

    const redirectToEcranGestion = () => {
        history.push(getPath(RoutesEcranGestion));
    };

    const onSubmit = (dataForm) => {
        if (dataForm && hasFormValid()) {
            const dataFormat = formaterModifierQuestionsReponses(dataForm);
            postApi(
                usagerClientReducer,
                API_MODIFIER_QUESTIONS_REPONSES,
                dataFormat,
                CONTEXT_API_MODIFIER_QUESTIONS_REPONSES,
            );
        }
        redirectToEcranGestion();
    };

    return (
        <Container>
            {isLoadingRecupererQuestions && <LoaderMi taille="lg" />}
            <Row>
                <Col col={12}>
                    <h1>Modification des questions et réponses secrètes de mon compte</h1>
                </Col>
            </Row>
            <Row>
                <Col col={12}>
                    <p>
                        Pour modifier les informations ci-dessous, veuillez sélectionner 3 questions
                        et saisir les réponses associées. Ces informations serviront en cas de en
                        cas de problème d&apos;accès à votre compte.
                    </p>
                </Col>
            </Row>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Row gutters>
                    <Col col={12} lg={6}>
                        <SelectQuestionsCompteUsager
                            label="Question 1 : "
                            name="question1"
                            register={register('question1', {
                                required: true,
                                validate: (value) =>
                                    verifierDoublonQuestion(
                                        value,
                                        getValues('question2'),
                                        getValues('question3'),
                                    ),
                            })}
                            state={formState.errors?.question1 ? 'error' : null}
                            message={formState.errors?.question1?.message}
                        />
                    </Col>
                    <Col col={12} lg={6}>
                        <InputControl
                            id="reponse1"
                            name="reponse1"
                            label="Réponse 1 : "
                            maxLength="50"
                            uppercase
                            {...register('reponse1', {
                                required: true,
                            })}
                        />
                    </Col>
                </Row>
                <Row gutters>
                    <Col col={12} lg={6}>
                        <SelectQuestionsCompteUsager
                            label="Question 2 : "
                            id="question2"
                            name="question2"
                            register={register('question2', {
                                required: true,
                                validate: (value) =>
                                    verifierDoublonQuestion(
                                        value,
                                        getValues('question1'),
                                        getValues('question3'),
                                    ),
                            })}
                            state={formState.errors?.question2 ? 'error' : null}
                            message={formState.errors?.question2?.message}
                        />
                    </Col>
                    <Col col={12} lg={6}>
                        <InputControl
                            name="reponse2"
                            id="reponse2"
                            label="Réponse 2 : "
                            maxLength="50"
                            uppercase
                            {...register('reponse2', {
                                required: true,
                            })}
                        />
                    </Col>
                </Row>
                <Row gutters>
                    <Col col={12} lg={6}>
                        <SelectQuestionsCompteUsager
                            label="Question 3 : "
                            name="question3"
                            register={register('question3', {
                                required: true,
                                validate: (value) =>
                                    verifierDoublonQuestion(
                                        value,
                                        getValues('question2'),
                                        getValues('question1'),
                                    ),
                            })}
                            state={formState.errors?.question3 ? 'error' : null}
                            message={formState.errors?.question3?.message}
                        />
                    </Col>
                    <Col col={12} lg={6}>
                        <InputControl
                            name="reponse3"
                            label="Réponse 3 : "
                            maxLength="50"
                            uppercase
                            {...register('reponse3', {
                                required: true,
                            })}
                        />
                    </Col>
                </Row>
                <Row className="margin-top-lg">
                    <Col col={12}>
                        <ButtonGroup align="right" inline>
                            <ButtonLink
                                as={Link}
                                to={getPath(RoutesEcranGestion)}
                                variant="secondary"
                                aria-label="Annuler et revenir à la gestion du profil"
                            >
                                Annuler
                            </ButtonLink>
                            <Button
                                name="valider"
                                variant="primary"
                                type="submit"
                                disabled={!hasFormValid()}
                                isLoading={isLoadingModifierQuestionsReponses}
                            >
                                Enregistrer les modifications
                            </Button>
                        </ButtonGroup>
                    </Col>
                </Row>
            </form>
        </Container>
    );
};

ModifierQuestionsReponses.propTypes = {
    postApi: PropTypes.func.isRequired,
    getApi: PropTypes.func.isRequired,
    reinitialiseApi: PropTypes.func.isRequired,
    isLoadingRecupererQuestions: PropTypes.bool,
    dataQuestions: PropTypes.object,
    isLoadingModifierQuestionsReponses: PropTypes.bool,
};

export default connect(
    (state) => {
        const { isLoading: isLoadingRecupererQuestions, data: dataQuestions } =
            state.monCompte.recupererQuestions;
        const {
            isLoading: isLoadingModifierQuestionsReponses,
            erreur: erreurModifierQuestionsReponses,
            message: messageModifierQuestionsReponses,
        } = state.monCompte.modifierQuestionsReponses;
        return {
            isLoadingRecupererQuestions,
            dataQuestions,
            isLoadingModifierQuestionsReponses,
            erreurModifierQuestionsReponses,
            messageModifierQuestionsReponses,
        };
    },
    {
        postApi: postAPI,
        getApi: getAPI,
        reinitialiseApi: reinitialiseAPI,
    },
)(ModifierQuestionsReponses);
